import { Shift, WorkSchedule } from "./types";

// ─── Overlap detection ────────────────────────────────────────────────────────

/** Convert a day+hour+minute into "minutes since start of week" for comparison. */
export function shiftToMinutes(dayOfWeek: number, hour: number, minute: number): number {
  return dayOfWeek * 1440 + hour * 60 + minute;
}

/** Returns true if shift A and shift B overlap in time. */
export function shiftsOverlap(a: Shift, b: Shift): boolean {
  const aStart = shiftToMinutes(a.startDayOfWeek, a.startHour, a.startMinute);
  const aEnd   = shiftToMinutes(a.endDayOfWeek,   a.endHour,   a.endMinute);
  const bStart = shiftToMinutes(b.startDayOfWeek, b.startHour, b.startMinute);
  const bEnd   = shiftToMinutes(b.endDayOfWeek,   b.endHour,   b.endMinute);
  // A shift where end <= start wraps overnight — treat it as invalid for now
  if (aEnd <= aStart || bEnd <= bStart) return false;
  return aStart < bEnd && aEnd > bStart;
}

/** Returns the set of shift indices that conflict with at least one other shift. */
export function findConflictingIndices(shifts: Shift[]): Set<number> {
  const conflicts = new Set<number>();
  for (let i = 0; i < shifts.length; i++) {
    for (let j = i + 1; j < shifts.length; j++) {
      if (shiftsOverlap(shifts[i], shifts[j])) {
        conflicts.add(i);
        conflicts.add(j);
      }
    }
  }
  return conflicts;
}

/** Returns true if the shift list has any overlapping windows. */
export function hasConflicts(shifts: Shift[]): boolean {
  return findConflictingIndices(shifts).size > 0;
}

// ─── Rule condition expansion ────────────────────────────────────────────────
// Converts a WorkSchedule's shifts into dayOfWeek + timeOfDay condition pairs
// that the rule evaluation backend already understands.

const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

/** Human-readable summary of a schedule's windows, e.g. "Mon–Fri 9:00–17:00". */
export function scheduleSummary(schedule: WorkSchedule): string {
  if (!schedule.shifts?.length) return "No shifts defined";
  return schedule.shifts
    .map((s) => {
      const start = `${String(s.startHour).padStart(2,"0")}:${String(s.startMinute).padStart(2,"0")}`;
      const end   = `${String(s.endHour).padStart(2,"0")}:${String(s.endMinute).padStart(2,"0")}`;
      if (s.startDayOfWeek === s.endDayOfWeek) {
        return `${DAY_NAMES[s.startDayOfWeek]} ${start}–${end}`;
      }
      return `${DAY_NAMES[s.startDayOfWeek]} ${start} – ${DAY_NAMES[s.endDayOfWeek]} ${end}`;
    })
    .join(", ");
}

/**
 * Returns an array of { dayOfWeekCondition, timeOfDayCondition } JSON strings
 * suitable for appending to a Rule's expression as SerializedCondition data.
 *
 * Each shift becomes one group of conditions (the rule engine ANDs within a group).
 * Multiple shifts are OR'd together via separate expression groups.
 */
export function expandScheduleToConditions(
  schedule: WorkSchedule
): Array<{ dayOfWeekData: string; timeOfDayData: string }> {
  return schedule.shifts.map((shift) => {
    // dayOfWeek condition: match the day(s) covered
    const days: string[] = [];
    let d = shift.startDayOfWeek;
    while (d !== shift.endDayOfWeek) {
      days.push(DAY_NAMES[d].toLowerCase());
      d = (d + 1) % 7;
    }
    days.push(DAY_NAMES[shift.endDayOfWeek].toLowerCase());

    const dayOfWeekData = JSON.stringify({ daysOfWeek: days });

    const from = `${String(shift.startHour).padStart(2,"0")}:${String(shift.startMinute).padStart(2,"0")}`;
    const to   = `${String(shift.endHour).padStart(2,"0")}:${String(shift.endMinute).padStart(2,"0")}`;
    const timeOfDayData = JSON.stringify({ from, to });

    return { dayOfWeekData, timeOfDayData };
  });
}

// ─── Team uniqueness ─────────────────────────────────────────────────────────

/**
 * Returns the schedule (if any) that currently has the given teamId assigned,
 * excluding the schedule being edited (by excludeId).
 */
export function findTeamScheduleConflict(
  schedules: WorkSchedule[],
  teamId: number,
  excludeId: number
): WorkSchedule | undefined {
  return schedules.find(
    (s) => s.id !== excludeId && (s.teamIds ?? []).includes(teamId)
  );
}
